<template>
  <div class="p-field" :class="{ 'p-float-label': labelStyle === 'float' }">
    <div :class="{ 'p-field-horizontal': labelStyle === 'inline' }">
      <!-- Label -->
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- ColorPicker Wrapper -->
      <div class="p-input-wrapper">
        <div class="flex items-center rounded">
          <ColorPicker ref="colorPickerRef" :id="id" v-model="localValue" :class="[
            'p-colorpicker',
            {
              'p-invalid': isInvalid,
              'p-disabled': disabled
            }
          ]" :disabled="disabled" :format="format" :inline="inline" :default-color="defaultColor" @change="onChange"
            @show="onShow" @hide="onHide" />
          <MOPInputText :id="`${id}-input`" v-model="localValue" :disabled="disabled" :placeholder="placeholder"
            :aria-label="label" :aria-invalid="isInvalid" :aria-required="required" :aria-describedby="ariaDescribedBy"
            :required="required" class="w-full ml-2" size="small" :name="name" />
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-helper-text">{{ helperText }}</small>
      <small v-for="(error, index) in localizedErrors" :key="index" :id="`${id}-error-${index}`" class="p-error block">
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { MOPColorPickerProps } from './types'

export default defineComponent({
  name: 'MOPColorPicker',
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import ColorPicker from 'primevue/colorpicker'
import type { ColorPickerChangeEvent } from 'primevue/colorpicker'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import type { ValidationRule } from '../../../../types/form'

const props = defineProps({
  modelValue: {
    type: String as () => string | null,
    default: null
  },
  defaultColor: {
    type: String,
    default: '#000000'
  },
  inline: {
    type: Boolean,
    default: false
  },
  format: {
    type: String as () => 'hex' | 'rgb' | 'hsb',
    default: 'hex'
  },
  invalid: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: ''
  },
  helperText: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  labelStyle: {
    type: String as () => 'fixed' | 'float' | 'inline',
    default: 'fixed'
  },
  id: {
    type: String,
    default: () => `mop-colorpicker-${Math.random().toString(36).substr(2, 9)}`
  },
  validationRules: {
    type: Array as () => ValidationRule[],
    default: () => []
  },
  validateOnBlur: {
    type: Boolean,
    default: true
  },
  validateOnInput: {
    type: Boolean,
    default: false
  },
  validateOnChange: {
    type: Boolean,
    default: true
  },
  showErrors: {
    type: Boolean,
    default: true
  },
  name: {
    type: String,
    default: undefined
  },
  formContext: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  change: [event: ColorPickerChangeEvent]
  show: []
  hide: []
  'validation-error': [errors: string[]]
  'validation-success': []
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const colorPickerRef = ref<InstanceType<typeof ColorPicker> | null>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const localValue = computed({
  get: () => {
    const value = props.modelValue ?? '';
    // Ensure the color value has a '#' prefix if it's a valid hex color
    if (value && value.match(/^[0-9A-Fa-f]{3,6}$/)) {
      return `#${value}`;
    }
    return value;
  },
  set: (newValue) => {
    // Ensure the color value has a '#' prefix when emitting
    let formattedValue = newValue;
    if (formattedValue && !formattedValue.startsWith('#') && formattedValue.match(/^[0-9A-Fa-f]{3,6}$/)) {
      formattedValue = `#${formattedValue}`;
    }
    emit('update:modelValue', formattedValue)
    if (formState && props.name) {
      formState.setFieldValue(props.name, formattedValue)
    }
  }
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onChange = (event: ColorPickerChangeEvent) => {
  // Ensure the color value has a '#' prefix
  if (event.value && !event.value.startsWith('#') && event.value.match(/^[0-9A-Fa-f]{3,6}$/)) {
    event.value = `#${event.value}`;
    localValue.value = event.value;
  }

  emit('change', event)
  isDirty.value = true
  if (props.validateOnChange) {
    validate()
  }
}

const onShow = () => {
  emit('show')
}

const onHide = async () => {
  emit('hide')
  isDirty.value = true
  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }
  if (props.validateOnBlur) {
    await validate()
  }
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

defineExpose({
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>
